/* =========================================================================
   Studio reskin for the embedded Yield Shooter — "Analysis Studio Live" look.

   This is an OVERRIDE stylesheet injected into the game's (same-origin) srcdoc
   iframe. It restyles the vendored bundle WITHOUT touching a single line of its
   logic — every game color flows through a known CSS class/selector (mapped from
   the game's original `global.css`), and the bundle ships ZERO `!important`
   rules, so these `!important` overrides win the cascade unconditionally and
   regardless of source order. SVG colors in the game are presentation
   attributes (`fill="…"`), which any CSS rule outranks.

   Palette mirrors AnalysisStudioLive's tokens (studio2.css):
     accent #0f5fd6 · positive #0e9f6e · negative #d14343 · warn #d48806
     text #1f2d44 · muted #5c6b7f · panel #fff · border #d8e1ef
     soft light gradient surface · 12–16px radii · soft shadows.

   The game's native theme (dark navy + arcade gold/indigo, Poppins) is remapped
   surface-by-surface to that clean, light, institutional command-center look.
   ========================================================================= */

export const STUDIO_SHOOTER_CSS = `
/* ---- Design tokens (scoped to the game root) --------------------------- */
.app-root {
  --st-accent:#0f5fd6; --st-accent-strong:#0a4fb0; --st-accent-soft:rgba(15,95,214,0.10);
  --st-positive:#0e9f6e; --st-positive-strong:#0c8a60;
  --st-negative:#d14343; --st-negative-strong:#b83636;
  --st-warn:#d48806; --st-warn-strong:#b87406;
  --st-text:#1f2d44; --st-muted:#5c6b7f;
  --st-panel:#ffffff; --st-border:#d8e1ef; --st-border-soft:rgba(216,225,239,0.7);
  --st-track:rgba(31,45,68,0.12); --st-slate:#9aa7bb; --st-slate-deep:#7e8aa0;
  --st-shadow:0 14px 30px rgba(19,33,68,0.10);
  --st-shadow-sm:0 2px 8px rgba(19,33,68,0.08);
}

/* ---- Base surfaces ------------------------------------------------------ */
.app-root { background:transparent !important; }
.app-master { color:var(--st-text) !important; }
.app-body {
  background:linear-gradient(180deg,#f7f9fc 0%,#eef3f8 100%) !important;
  background-size:cover !important;
}
.app-root, .app-root button { font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,'Poppins',sans-serif; }

/* ---- Header / HUD bar --------------------------------------------------- */
.game-header {
  background:rgba(255,255,255,0.9) !important;
  border-bottom:1px solid var(--st-border-soft) !important;
  border-right-color:var(--st-border-soft) !important; /* narrow-landscape rail divider */
}
.game-slots { border-bottom-color:var(--st-border) !important; }

/* ---- Title flag (angled banner) → studio accent ------------------------ */
.game-title {
  background-color:var(--st-accent) !important;
  border-color:var(--st-accent) !important;
  border-radius:6px;
}
.game-title .traderion-logo { display:none !important; }      /* drop 3rd-party brand */
.game-title .game-name { color:#ffffff !important; letter-spacing:0.04em; }
.game-level { color:var(--st-text) !important; }
.game-level .label { color:var(--st-muted) !important; text-transform:uppercase; letter-spacing:0.05em; }
.game-heading { background-color:transparent !important; }

/* ---- Buttons (.icon-btn / .text-btn) ----------------------------------- */
/* Color is driven by a per-variant --btn token so hover/focus/active keep the
   variant identity instead of the game's universal gold flash. */
.app-root .icon-btn .body, .app-root .text-btn .body {
  background-color:var(--btn) !important;
  border-color:var(--btn) !important;
  color:var(--btn-fg,#fff) !important;
}
.app-root .icon-btn .icon, .app-root .text-btn .icon { color:var(--btn-fg,#fff) !important; }
.app-root .icon-btn:before, .app-root .text-btn:before { background-color:var(--btn-shadow,rgba(19,33,68,0.16)) !important; }

.app-root .icon-btn:hover .body, .app-root .text-btn:hover .body,
.app-root .icon-btn:focus .body, .app-root .text-btn:focus .body,
.app-root .icon-btn:active .body, .app-root .text-btn:active .body {
  background-color:var(--btn-strong,var(--btn)) !important;
  border-color:var(--btn-strong,var(--btn)) !important;
  color:var(--btn-fg,#fff) !important;
}
.app-root .icon-btn:hover:before, .app-root .text-btn:hover:before,
.app-root .icon-btn:focus:before, .app-root .text-btn:focus:before,
.app-root .icon-btn:active:before, .app-root .text-btn:active:before {
  background-color:var(--btn-shadow,rgba(19,33,68,0.16)) !important;
}
.app-root .icon-btn[disabled] .body, .app-root .icon-btn[aria-disabled="true"] .body,
.app-root .text-btn[disabled] .body, .app-root .text-btn[aria-disabled="true"] .body {
  background-color:#c7d3e6 !important; border-color:#c7d3e6 !important; color:rgba(255,255,255,0.85) !important;
}

/* variant tokens (declared after the base so they win the equal-specificity tie) */
.app-root .icon-btn, .app-root .text-btn { --btn:var(--st-accent); --btn-strong:var(--st-accent-strong); --btn-shadow:rgba(10,49,99,0.30); --btn-fg:#fff; }
.app-root .primary   { --btn:var(--st-accent); --btn-strong:var(--st-accent-strong); --btn-shadow:rgba(10,49,99,0.30); --btn-fg:#fff; }
.app-root .success   { --btn:var(--st-positive); --btn-strong:var(--st-positive-strong); --btn-shadow:rgba(11,84,58,0.30); --btn-fg:#fff; }
.app-root .danger    { --btn:var(--st-negative); --btn-strong:var(--st-negative-strong); --btn-shadow:rgba(120,40,40,0.30); --btn-fg:#fff; }
.app-root .warning   { --btn:var(--st-warn); --btn-strong:var(--st-warn-strong); --btn-shadow:rgba(120,74,6,0.30); --btn-fg:#fff; }
.app-root .info      { --btn:var(--st-accent); --btn-strong:var(--st-accent-strong); --btn-shadow:rgba(10,49,99,0.30); --btn-fg:#fff; }
.app-root .secondary { --btn:#e6ecf5; --btn-strong:#d8e1ef; --btn-shadow:rgba(31,45,68,0.12); --btn-fg:#1f2d44; }
.app-root .dark      { --btn:var(--st-text); --btn-strong:#16223a; --btn-shadow:rgba(10,16,30,0.30); --btn-fg:#fff; }
.app-root .light     { --btn:#eef3f8; --btn-strong:#e2e9f3; --btn-shadow:rgba(31,45,68,0.10); --btn-fg:#1f2d44; }

/* outline variant (the in-game menu button) */
.app-root .icon-btn.outline .body, .app-root .text-btn.outline .body {
  background-color:transparent !important; border-color:var(--st-border) !important; color:var(--st-accent) !important;
}
.app-root .icon-btn.outline .icon, .app-root .text-btn.outline .icon { color:var(--st-accent) !important; }
.app-root .icon-btn.outline:hover .body, .app-root .text-btn.outline:hover .body,
.app-root .icon-btn.outline:focus .body, .app-root .text-btn.outline:focus .body {
  background-color:var(--st-accent-soft) !important; border-color:var(--st-accent) !important; color:var(--st-accent) !important;
}
.app-root .icon-btn.outline:hover .icon, .app-root .text-btn.outline:hover .icon { color:var(--st-accent) !important; }

/* select control (language picker etc.) */
.select { border-color:var(--st-border) !important; color:var(--st-text) !important; background:#fff !important; border-radius:10px !important; }
.select:hover:not([disabled]) { border-color:var(--st-accent) !important; }

/* Flatten the arcade extruded "lip" (:before) and tame the 1.3x hover pop →
   soft, flat studio buttons with a diffuse shadow and a subtle hover. */
.app-root .icon-btn:before, .app-root .text-btn:before,
.app-root .icon-btn:hover:before, .app-root .text-btn:hover:before,
.app-root .icon-btn:focus:before, .app-root .text-btn:focus:before,
.app-root .icon-btn:active:before, .app-root .text-btn:active:before {
  box-shadow:none !important; background-color:transparent !important;
}
.app-root .icon-btn .body, .app-root .text-btn .body { box-shadow:var(--st-shadow-sm) !important; }
.app-root .icon-btn:hover { transform:scale(1.06) !important; }
.app-root .text-btn:hover { transform:scale(1.04) !important; }

/* ---- Indicators (score / time / tokens / lives / bullets) -------------- */
.indicator .value { color:var(--st-text) !important; font-variant-numeric:tabular-nums !important; }
.indicator.boxed {
  background:#ffffff !important;
  border:1px solid var(--st-border) !important;
  border-radius:14px !important;
  box-shadow:var(--st-shadow-sm) !important;
}
.indicator.primary .icon   { color:var(--st-accent) !important; }
.indicator.success .icon   { color:var(--st-positive) !important; }
.indicator.danger .icon    { color:var(--st-negative) !important; }
.indicator.warning .icon   { color:var(--st-warn) !important; }
.indicator.info .icon      { color:var(--st-accent) !important; }
.indicator.dark .icon      { color:var(--st-text) !important; }
.indicator .value { background:transparent !important; }   /* neutralise mobile dark value chip */

/* Time HUD glyph. The game renders the HOURGLASS, but a bundle copy-paste bug
   tags it .fuel-dynamic-svg. Recolor the drained track to a studio slate and the
   (mostly covered) outline frame to muted — but NEVER the <mask> path: its
   fill="white" defines mask coverage, so recolouring it would fade the whole
   glyph. The dynamic .hg-fill (red→green time-remaining) is a functional signal
   and is left intact. (The .pump-* rule covers the fuel glyph if ever shown.) */
.app-root svg.fuel-dynamic-svg .hg-bg,
.app-root svg.fuel-dynamic-svg .pump-bg { fill:var(--st-slate) !important; }
.app-root svg.fuel-dynamic-svg > path:not([fill]) { fill:var(--st-muted) !important; }

/* ---- Progress bar (accuracy) ------------------------------------------- */
.progress-bar { background:var(--st-track) !important; }
.progress-bar:after { opacity:0 !important; }
.progress-bar .fill, .progress-bar.primary .fill { background-color:var(--st-accent) !important; }
.progress-bar.success .fill { background-color:var(--st-positive) !important; }
.progress-bar.danger .fill  { background-color:var(--st-negative) !important; }
.progress-bar.warning .fill { background-color:var(--st-warn) !important; }
.progress-bar .target { border-left-color:var(--st-muted) !important; }
/* The centred % label spans BOTH the colored fill and the light empty track, so
   use dark text with a white halo to stay legible over either zone. */
.progress-bar .value {
  color:var(--st-text) !important; font-variant-numeric:tabular-nums !important;
  text-shadow:0 0 3px rgba(255,255,255,0.95), 0 1px 2px rgba(255,255,255,0.85) !important;
}

/* accuracy labels (were white) */
.accuracy-indicator .label, .accuracy-indicator .target { color:var(--st-text) !important; }
.accuracy-indicator .target-label { color:var(--st-muted) !important; }
.accuracy-indicator .target-value { font-variant-numeric:tabular-nums !important; }

/* ---- Slots (the core targets) ------------------------------------------ */
.slot-target-svg { fill:var(--st-slate) !important; }
.slot-card.idle .slot-target-svg,
.slot-card.loading .slot-target-svg,
.slot-card.waiting .slot-target-svg { fill:var(--st-slate) !important; }
.slot-card.idle .target-body-shadow-path,
.slot-card.loading .target-body-shadow-path,
.slot-card.waiting .target-body-shadow-path { fill:var(--st-slate-deep) !important; }

/* the live "bond" target you shoot → studio accent (neutral live) */
.slot-card.bond .slot-target-svg { fill:var(--st-accent) !important; }
.slot-card.bond .shadow-path { fill:var(--st-accent-strong) !important; }

/* correct feedback → studio green */
.slot-card.feedback[class*="correct-"] .slot-target-svg { fill:var(--st-positive-strong) !important; }
.slot-card.feedback[class*="correct-"] .target-body-shadow-path { fill:#0a6f4a !important; }
.slot-card.feedback[class*="correct-"] .state-message { background-color:var(--st-positive) !important; color:#fff !important; }
.slot-card.feedback.correct-action .arrow-path { fill:var(--st-positive) !important; }

/* incorrect feedback → studio red */
.slot-card.feedback[class*="incorrect-"] .slot-target-svg { fill:var(--st-negative-strong) !important; }
.slot-card.feedback[class*="incorrect-"] .target-body-shadow-path { fill:#8f2f2f !important; }
.slot-card.feedback[class*="incorrect-"] .state-message { background-color:var(--st-negative) !important; color:#fff !important; }
.slot-card.feedback.incorrect-action .arrow-path { fill:var(--st-negative) !important; }

/* slot status pill (loading / waiting) */
.slot-card .state-message { background:#e9eef6 !important; color:var(--st-text) !important; border-radius:12px !important; }
.slot-card.loading .state-message, .slot-card.waiting .state-message { color:var(--st-positive) !important; }

/* Slot cards carry NO fill in the default (landscape) layout — so hovering a
   target scales only the target silhouette, never a visible rectangle that
   overlaps its neighbours. The game only fills the card in its narrow/portrait
   wrap layout; there, recolor the original dark box to a faint studio tint. */
@media (max-aspect-ratio: 3/4) {
  .game-slots .slot-card { background:rgba(31,45,68,0.04) !important; }
}
@media (min-aspect-ratio: 3/4) and (max-width: 768px),
       (min-aspect-ratio: 3/4) and (max-height: 768px) {
  .game-slots .slot-card { background:rgba(31,45,68,0.04) !important; }
}

/* ---- Bond detail text -------------------------------------------------- */
/* These fields are overlaid ON the accent-blue live target body, so they use
   light, high-contrast type (translucent-white labels + solid-white values with
   a soft shadow) — the studio treatment for labels on a coloured surface. */
.bond-details .field .name {
  color:rgba(255,255,255,0.92) !important; text-transform:uppercase;
  letter-spacing:0.04em; font-size:0.82em;
  text-shadow:0 1px 2px rgba(8,38,86,0.5) !important;
}
.bond-details .field .value {
  color:#ffffff !important; font-variant-numeric:tabular-nums; font-weight:700 !important;
  text-shadow:0 1px 2px rgba(8,38,86,0.5) !important;
}

/* ---- In-game message popup --------------------------------------------- */
.game-message .message-box { background:rgba(31,45,68,0.18) !important; }
.game-message .message {
  background:#ffffff !important; color:var(--st-text) !important;
  border:2px solid var(--st-warn) !important; border-radius:12px !important;
  box-shadow:var(--st-shadow) !important;
}

/* ---- Modal windows (menu / level passed / failed / completed / shop) --- */
.window { color:var(--st-text) !important; }
.window > .body { border:1px solid var(--st-border) !important; border-radius:14px !important; box-shadow:var(--st-shadow) !important; }
.window p, .window .reason, .window-content > .row { color:var(--st-text) !important; }
.window .note { color:var(--st-muted) !important; }
.window .separator { border-bottom:1px solid var(--st-border-soft) !important; }
.window .score { color:var(--st-positive) !important; font-variant-numeric:tabular-nums !important; }
.window .score-label { color:var(--st-muted) !important; opacity:1 !important; }

/* window header band — default + per-variant (accent / pos / neg / warn) */
.app-root .window-header > .body { background-color:var(--st-accent) !important; }
.app-root .window-header:before  { background-color:var(--st-accent-strong) !important; }
.app-root .window-header h3      { color:#ffffff !important; }
.app-root .window.success .window-header > .body { background-color:var(--st-positive) !important; }
.app-root .window.success .window-header:before  { background-color:var(--st-positive-strong) !important; }
.app-root .window.danger .window-header > .body  { background-color:var(--st-negative) !important; }
.app-root .window.danger .window-header:before   { background-color:var(--st-negative-strong) !important; }
.app-root .window.warning .window-header > .body { background-color:var(--st-warn) !important; }
.app-root .window.warning .window-header:before  { background-color:var(--st-warn-strong) !important; }
.app-root .window.info .window-header > .body    { background-color:var(--st-accent) !important; }
.app-root .window.info .window-header:before     { background-color:var(--st-accent-strong) !important; }
.app-root .window.primary .window-header > .body { background-color:var(--st-accent) !important; }
.app-root .window.primary .window-header:before  { background-color:var(--st-accent-strong) !important; }

/* window header text colour rows */
.app-root .window .window-content > .row.primary, .app-root .window .window-content > .row.primary .reason { color:var(--st-accent) !important; }
.app-root .window .window-content > .row.success, .app-root .window .window-content > .row.success .reason { color:var(--st-positive) !important; }
.app-root .window .window-content > .row.danger,  .app-root .window .window-content > .row.danger .reason  { color:var(--st-negative) !important; }
.app-root .window .window-content > .row.warning, .app-root .window .window-content > .row.warning .reason { color:var(--st-warn) !important; }
.app-root .window .window-content > .row.info,    .app-root .window .window-content > .row.info .reason    { color:var(--st-accent) !important; }

/* shop / upgrades table */
.window-table > .row { border-bottom-color:var(--st-border) !important; }
.window-table > .row:hover { background-color:var(--st-accent-soft) !important; color:var(--st-accent) !important; }
.window-table .icon { border-color:var(--st-border) !important; }
.window-footer { background:#ffffff !important; }
.window > .shadow { background-color:#eef3f8 !important; box-shadow:none !important; }

/* ---- Star rating (level-passed) → studio amber ------------------------- */
.app-root .star-indicator path#star { fill:var(--st-warn) !important; }
.app-root .star-indicator path#shadow-3d { fill:var(--st-warn-strong) !important; }

/* ---- Stat count-change pulse glow → studio positive/negative ----------- */
/* @keyframes can't take !important and the game's stylesheet loads AFTER ours,
   so we can't override its same-named keyframes. Instead, repoint the animations
   at uniquely-named studio keyframes via animation-name (which DOES win), keeping
   the game's duration/iteration from its shorthand. */
@keyframes st-pulse-green {
  0%   { box-shadow:0 0 0 0 var(--st-positive); opacity:0; }
  20%  { box-shadow:0 0 20px 40px var(--st-positive); opacity:0.5; }
  100% { box-shadow:0 0 30px 60px var(--st-positive); opacity:0; }
}
@keyframes st-pulse-red {
  0%   { box-shadow:0 0 0 0 var(--st-negative); opacity:0; }
  20%  { box-shadow:0 0 20px 40px var(--st-negative); opacity:0.5; }
  100% { box-shadow:0 0 30px 60px var(--st-negative); opacity:0; }
}
.app-root .indicator.increase .icon:after { animation-name:st-pulse-green !important; }
.app-root .indicator.decrease .icon:after { animation-name:st-pulse-red !important; }
`;

export default STUDIO_SHOOTER_CSS;
